import type { ThemeEffect } from '@workspace/design-tokens';
import { DEFAULT_THEME_EFFECT, isThemeEffect } from '@workspace/design-tokens';

const THEME_CLASSNAMES = [
  'light-theme',
  'dark-theme',
  'midnight-theme',
  'purple-dark-theme',
  'bubblegum-theme',
  'ocean-theme',
  'forest-theme',
  'aurora-theme',
  'ember-theme',
  'citrus-theme',
  'sandstone-theme',
  'slate-theme',
  'glacier-theme',
  'cyberpunk-theme',
  'rainbow-theme',
  'retro-theme',
  'noir-theme'
] as const;

const THEME_CLASS_MAP: Record<string, string | null> = {
  auto: null,
  light: 'light-theme',
  dark: 'dark-theme',
  midnight: 'midnight-theme',
  'purple-dark': 'purple-dark-theme',
  bubblegum: 'bubblegum-theme',
  ocean: 'ocean-theme',
  forest: 'forest-theme',
  aurora: 'aurora-theme',
  ember: 'ember-theme',
  citrus: 'citrus-theme',
  sandstone: 'sandstone-theme',
  slate: 'slate-theme',
  glacier: 'glacier-theme',
  cyberpunk: 'cyberpunk-theme',
  rainbow: 'rainbow-theme',
  retro: 'retro-theme',
  noir: 'noir-theme'
};

const DARK_THEMES = new Set(['dark', 'midnight', 'purple-dark', 'aurora', 'ember', 'slate', 'cyberpunk', 'noir']);
const EFFECT_STORAGE_KEY = 'theme_effect';

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export class ThemeController {
  private static PREVIEW_MODE_KEY = 'theme_preview_mode';

  private prefersDarkScheme: MediaQueryList | null = null;

  private legacyPreferenceListener: ((event: MediaQueryListEvent | MediaQueryList) => void) | null = null;

  private currentTheme: string = 'auto';
  private currentEffect: ThemeEffect = DEFAULT_THEME_EFFECT;

  constructor(private root: HTMLElement = document.documentElement) {
    if (typeof window === 'undefined') {
      throw new Error('ThemeController requires a browser environment');
    }

    this.currentTheme = this.getSavedTheme();
    this.currentEffect = this.getSavedEffect();

    console.log('[ThemeController] constructor', {
      currentTheme: this.currentTheme,
      rootClasses: this.root.className
    });

    window.addEventListener('storage', this.handleStorageEvent);

    if (typeof window.matchMedia === 'function') {
      this.prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');
      this.legacyPreferenceListener = event => {
        const matches = (event as MediaQueryList).matches ?? this.prefersDarkScheme?.matches ?? false;
        console.log('[ThemeController] system preference change', {
          matches,
          preview: this.isInPreviewMode(),
          currentTheme: this.currentTheme
        });
        if (this.currentTheme === 'auto' && !this.isInPreviewMode()) {
          this.applyTheme('auto', false);
        }
      };
      const listener = this.legacyPreferenceListener;
      if (this.prefersDarkScheme.addEventListener) {
        this.prefersDarkScheme.addEventListener('change', listener);
      } else if (this.prefersDarkScheme.addListener) {
        this.prefersDarkScheme.addListener(listener);
      }
    }

    this.applyTheme(this.currentTheme, false);
    this.applyThemeEffect(this.currentEffect, false);
  }

  async init(force: boolean = false) {
    const inPreview = this.isInPreviewMode();
    console.log('[ThemeController] init:start', {
      force,
      inPreview,
      currentTheme: this.currentTheme
    });

    if (inPreview && !force) {
      console.log('[ThemeController] init:skip due to preview mode');
      return;
    }

    let savedTheme = 'auto';
    try {
      const response = await fetch('/api/v1/preferences/effective?keys=ui.theme', {
        credentials: 'include',
        headers: {
          'X-App-ID': 'dashboard'
        }
      });

      if (response.ok) {
        const data = await response.json();
        savedTheme = (data.keys?.['ui.theme']?.value as string | undefined) ?? 'auto';
        localStorage.setItem('theme', savedTheme);
        console.log('[ThemeController] init:loaded server preference', { savedTheme });
      } else {
        savedTheme = this.getSavedTheme();
        console.log('[ThemeController] init:server preference failed, using saved theme', {
          status: response.status,
          savedTheme
        });
      }
    } catch (error) {
      savedTheme = this.getSavedTheme();
      console.warn('[ThemeController] init:error fetching server preference, using saved theme', error);
    }

    console.log('[ThemeController] init:apply theme', { savedTheme });
    this.applyTheme(savedTheme, false);
  }

  setTheme(name: string, persist: boolean = true) {
    console.log('[ThemeController] setTheme', { name, persist });
    this.applyTheme(name, persist);
  }

  previewTheme(name: string) {
    console.log('[ThemeController] previewTheme', { name });
    sessionStorage.setItem(ThemeController.PREVIEW_MODE_KEY, 'true');
    this.setTheme(name, false);
  }

  saveTheme(name: string) {
    const previous = this.getSavedTheme();
    console.log('[ThemeController] saveTheme', { name, previous });
    sessionStorage.removeItem(ThemeController.PREVIEW_MODE_KEY);
    this.setTheme(name, true);
    try {
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: 'theme',
          newValue: name,
          oldValue: previous,
          storageArea: localStorage
        })
      );
    } catch (error) {
      console.warn('[ThemeController] saveTheme:failed to dispatch storage event', error);
    }
  }

  getTheme(): string {
    return this.currentTheme;
  }

  getSavedTheme(): string {
    if (typeof window === 'undefined') {
      return 'auto';
    }
    try {
      return localStorage.getItem('theme') || 'auto';
    } catch (error) {
      console.warn('[ThemeController] getSavedTheme:failed to read localStorage', error);
      return 'auto';
    }
  }

  setEffect(effect: ThemeEffect, persist: boolean = true) {
    console.log('[ThemeController] setEffect', { effect, persist });
    this.applyThemeEffect(effect, persist);
  }

  saveEffect(effect: ThemeEffect) {
    const previous = this.getSavedEffect();
    console.log('[ThemeController] saveEffect', { effect, previous });
    this.setEffect(effect, true);
    try {
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: EFFECT_STORAGE_KEY,
          newValue: effect,
          oldValue: previous,
          storageArea: localStorage
        })
      );
    } catch (error) {
      console.warn('[ThemeController] saveEffect:failed to dispatch storage event', error);
    }
  }

  getEffect(): ThemeEffect {
    return this.currentEffect;
  }

  getSavedEffect(): ThemeEffect {
    if (typeof window === 'undefined') {
      return DEFAULT_THEME_EFFECT;
    }
    try {
      const stored = localStorage.getItem(EFFECT_STORAGE_KEY);
      if (stored && isThemeEffect(stored)) {
        return stored;
      }
      return DEFAULT_THEME_EFFECT;
    } catch (error) {
      console.warn('[ThemeController] getSavedEffect:failed to read localStorage', error);
      return DEFAULT_THEME_EFFECT;
    }
  }

  exitPreviewMode() {
    console.log('[ThemeController] exitPreviewMode');
    sessionStorage.removeItem(ThemeController.PREVIEW_MODE_KEY);
  }

  isInPreviewMode(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      return sessionStorage.getItem(ThemeController.PREVIEW_MODE_KEY) === 'true';
    } catch (error) {
      console.warn('[ThemeController] isInPreviewMode:failed to read sessionStorage', error);
      return false;
    }
  }

  private handleStorageEvent = (event: StorageEvent) => {
    console.log('[ThemeController] storage event', {
      key: event.key,
      newValue: event.newValue,
      preview: this.isInPreviewMode()
    });
    if (event.key === 'theme' && event.newValue !== null && !this.isInPreviewMode()) {
      this.setTheme(event.newValue, false);
    }
    if (event.key === EFFECT_STORAGE_KEY && event.newValue !== null && isThemeEffect(event.newValue)) {
      this.setEffect(event.newValue, false);
    }
  };

  private applyTheme(name: string, persist: boolean) {
    const resolved = this.resolveTheme(name);
    console.log('[ThemeController] applyTheme:start', {
      name,
      persist,
      resolved
    });

    this.currentTheme = name;

    const { className, dataThemeValue, darkMode } = resolved;

    this.root.classList.remove(...THEME_CLASSNAMES);
    if (className) {
      this.root.classList.add(className);
    }

    const body = document.body;
    if (body) {
      body.classList.remove(...THEME_CLASSNAMES);
      if (className) {
        body.classList.add(className);
      }
      body.classList.toggle('dark', darkMode);
      body.setAttribute('data-theme', dataThemeValue);
      body.style.colorScheme = darkMode ? 'dark' : 'light';
    }

    this.root.setAttribute('data-theme', dataThemeValue);
    this.root.classList.toggle('dark', darkMode);
    this.root.style.colorScheme = darkMode ? 'dark' : 'light';

    if (persist) {
      try {
        localStorage.setItem('theme', name);
      } catch (error) {
        console.warn('[ThemeController] applyTheme:failed to persist theme', error);
      }
    }

    let storedTheme: string | null = null;
    try {
      storedTheme = localStorage.getItem('theme');
    } catch {
      storedTheme = null;
    }

    console.log('[ThemeController] applyTheme:after', {
      name,
      rootClasses: this.root.className,
      bodyClasses: body?.className ?? null,
      dataThemeValue,
      darkMode,
      storedTheme,
      persist
    });

    this.root.dispatchEvent(new CustomEvent('themechange', { detail: { name, persist } }));
  }

  private resolveTheme(name: string) {
    if (name === 'auto') {
      const prefersDark = this.prefersDarkScheme?.matches ?? getSystemPrefersDark();
      console.log('[ThemeController] resolveTheme:auto', { prefersDark });
      return {
        className: prefersDark ? 'dark-theme' : 'light-theme',
        dataThemeValue: 'auto',
        darkMode: prefersDark
      };
    }

    const className = THEME_CLASS_MAP[name] || null;
    const darkMode = DARK_THEMES.has(name);
    console.log('[ThemeController] resolveTheme:explicit', {
      name,
      className,
      darkMode
    });
    return {
      className,
      dataThemeValue: name,
      darkMode
    };
  }

  private applyThemeEffect(effect: ThemeEffect, persist: boolean) {
    const target = effect ?? DEFAULT_THEME_EFFECT;
    this.currentEffect = target;
    const body = document.body;
    Array.from(this.root.classList)
      .filter(className => className.startsWith('theme-effect-'))
      .forEach(className => this.root.classList.remove(className));
    this.root.setAttribute('data-theme-effect', target);
    if (target !== 'none') {
      this.root.classList.add(`theme-effect-${target}`);
    }
    if (body) {
      body.setAttribute('data-theme-effect', target);
      Array.from(body.classList)
        .filter(className => className.startsWith('theme-effect-'))
        .forEach(className => body.classList.remove(className));
      if (target !== 'none') {
        body.classList.add(`theme-effect-${target}`);
      }
    }

    if (persist) {
      try {
        localStorage.setItem(EFFECT_STORAGE_KEY, target);
      } catch (error) {
        console.warn('[ThemeController] applyThemeEffect:failed to persist effect', error);
      }
    }
  }
}
